import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { STOCK_IMAGES } from '../../config/constants';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { storageService } from '../../services/storageService';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { SubscriptionService } from '../../services/subscriptionService';
import { MediaAsset } from '../../types';
import { AlertDialog } from '../common/AlertDialog';
import { DeleteService } from '../../services/deleteService';
import {
  Image as ImageIcon,
  Upload,
  Copy,
  Check,
  HardDrive,
  ExternalLink,
  Trash2,
  Loader2,
  AlertCircle,
  Search,
} from 'lucide-react';

export const MediaManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'uploaded' | 'stock'>('uploaded');
  const [uploadedAssets, setUploadedAssets] = useState<MediaAsset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    assetId: string;
    assetName: string;
    storagePath?: string;
    loading: boolean;
  }>({
    open: false,
    assetId: '',
    assetName: '',
    loading: false,
  });

  // Load site media from database
  useEffect(() => {
    if (!activeSite?.id) return;
    let isMounted = true;
    const loadMedia = async () => {
      setIsLoading(true);
      try {
        const items = await dbService.getSiteMedia(activeSite.id);
        if (isMounted) {
          setUploadedAssets(items || []);
        }
      } catch (err) {
        console.warn('Failed to load media assets:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Medya Kütüphanesi İçin Aktif Web Sitesi Gereklidir"
        description="Görsel yüklemek, stok fotoğraf galerisini kullanmak ve depolama alanınızı yönetmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini seçin."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const planQuotas = SubscriptionService.getPlanQuotas(activeSite.subscription?.planId);
  const maxMediaLimit = planQuotas.maxMediaAssets || 200;
  const currentMediaCount = uploadedAssets.length;
  const isMediaLimitReached = currentMediaCount >= maxMediaLimit;

  const storageUsedMB = activeSite.storageUsedMB || Math.round(uploadedAssets.reduce((acc, m) => acc + (m.sizeBytes || 0), 0) / (1024 * 1024)) || 0;
  const storageLimitMB = Math.round(planQuotas.maxStorageBytes / (1024 * 1024)) || 10240;
  const storagePercentage = Math.min(100, Math.round((storageUsedMB / storageLimitMB) * 100));

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeSite) return;
    setUploadError(null);

    // 1. Quota checks
    const quotaCheck = await SubscriptionService.checkResourceQuota(
      activeSite.id,
      'media',
      uploadedAssets.length,
      file.size
    );

    if (!quotaCheck.allowed) {
      setUploadError(quotaCheck.reason || `Paketinizin medya limiti (${maxMediaLimit} adet) dolmuştur.`);
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setUploadError('Dosya boyutu 25MB sınırını aşamaz.');
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await storageService.uploadSiteMedia(activeSite.id, file);
      const newAsset = await dbService.addMediaAsset(activeSite.id, {
        siteId: activeSite.id,
        name: file.name,
        url: uploadResult.url,
        sizeBytes: file.size,
        mimeType: file.type || 'image/jpeg',
      });

      setUploadedAssets((prev) => [newAsset, ...prev]);
      const addedMB = Math.max(1, Math.round(file.size / (1024 * 1024)));
      updateSiteData({ storageUsedMB: storageUsedMB + addedMB });
    } catch (err: any) {
      console.error('Media upload error:', err);
      setUploadError('Görsel yüklenirken bir hata oluştu: ' + (err.message || 'Lütfen tekrar deneyin.'));
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleDeleteClick = (asset: MediaAsset) => {
    setDeleteDialog({
      open: true,
      assetId: asset.id,
      assetName: asset.name || 'Görsel',
      storagePath: asset.storagePath,
      loading: false,
    });
  };

  const handleExecuteDelete = async () => {
    if (!activeSite || !deleteDialog.assetId || deleteDialog.loading) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      const result = await DeleteService.deleteMedia(
        activeSite.id,
        deleteDialog.assetId,
        deleteDialog.storagePath
      );
      if (result.success) {
        setUploadedAssets((prev) => prev.filter((a) => a.id !== deleteDialog.assetId));
        setDeleteDialog({ open: false, assetId: '', assetName: '', loading: false });
        setUploadError(null);
      } else {
        setUploadError(result.error || 'Görsel silinemedi.');
        setDeleteDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (err: any) {
      setUploadError(err.message || 'Silme işlemi sırasında hata oluştu.');
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const filteredAssets = uploadedAssets.filter((a) =>
    (a.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredStock = STOCK_IMAGES.filter((s) =>
    (s.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-purple-600">
            <ImageIcon className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Medya ve Varlık Kütüphanesi</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Görsel ve Dosya Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Web sitenizde kullanacağınız tüm fotoğrafları yükleyin, optimize edilmiş CDN linklerini kopyalayın.
          </p>
        </div>

        {/* Storage Meter Gauge */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 text-xs">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] text-slate-500 font-semibold">Medya Kotası</div>
              <div className="font-bold text-slate-800">
                {currentMediaCount} <span className="text-slate-400 font-normal">/ {maxMediaLimit} adet</span>
              </div>
            </div>
          </div>

          <div className="p-3 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 text-xs">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <HardDrive className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-slate-800">
                {storageUsedMB} MB <span className="text-slate-400 font-normal">/ {(storageLimitMB / 1024).toFixed(0)} GB</span>
              </div>
              <div className="w-28 bg-slate-100 h-2 rounded-full mt-1.5 overflow-hidden">
                <div className="bg-purple-600 h-full rounded-full" style={{ width: `${storagePercentage}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadError && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="font-medium">{uploadError}</span>
        </div>
      )}

      {/* Upload Dropzone */}
      <div
        className={`p-8 border-2 border-dashed rounded-2xl bg-white text-center space-y-3 transition group ${
          isMediaLimitReached
            ? 'border-slate-200 opacity-60 pointer-events-none'
            : 'border-slate-300 hover:border-purple-500'
        }`}
      >
        <div className="w-12 h-12 rounded-full bg-purple-50 text-purple-600 mx-auto flex items-center justify-center group-hover:scale-110 transition">
          {isUploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
        </div>
        <div>
          <h3 className="font-bold text-sm text-slate-800">
            {isMediaLimitReached ? 'Paketinizin Medya Limiti Doldu' : 'Fotoğraflarınızı Buraya Yükleyin'}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, WEBP veya SVG formatları (Maks. 25MB)</p>
        </div>
        <div>
          <label
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold shadow-md cursor-pointer transition ${
              isMediaLimitReached || isUploading
                ? 'bg-slate-300 text-slate-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
              </>
            ) : (
              'Bilgisayardan Dosya Seç'
            )}
            <input
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*"
              disabled={isMediaLimitReached || isUploading}
            />
          </label>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-slate-200 pb-3">
        {/* Media Tabs */}
        <div className="flex gap-6 text-xs font-bold w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('uploaded')}
            className={`pb-3 border-b-2 transition ${
              activeTab === 'uploaded'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            Yüklenen Dosyalarım ({uploadedAssets.length})
          </button>
          <button
            onClick={() => setActiveTab('stock')}
            className={`pb-3 border-b-2 transition ${
              activeTab === 'stock'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            Hazır Stok Fotoğraflar ({STOCK_IMAGES.length})
          </button>
        </div>

        {/* Search input */}
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Görsel ara..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* Media Grid */}
      {activeTab === 'uploaded' ? (
        isLoading ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
            Yüklenen görseller yükleniyor...
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-white space-y-2">
            <ImageIcon className="w-8 h-8 text-slate-300 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">Henüz Görsel Yüklenmedi</h4>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Yukarıdaki alandan web siteniz için fotoğraflar yükleyebilir ve tasarım editöründe kullanabilirsiniz.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition group flex flex-col justify-between"
              >
                <div className="relative h-36 overflow-hidden bg-slate-100">
                  <img
                    src={asset.url}
                    alt={asset.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition">
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(asset)}
                      title="Sil"
                      className="p-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 shadow"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleCopy(asset.url)}
                    className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-slate-900/80 hover:bg-slate-900 text-white shadow text-[10px] font-semibold flex items-center gap-1 transition"
                  >
                    {copiedUrl === asset.url ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    {copiedUrl === asset.url ? 'Kopyalandı' : 'URL Kopyala'}
                  </button>
                </div>
                <div className="p-2.5 text-slate-700 text-xs">
                  <div className="font-semibold truncate">{asset.name || 'Görsel'}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {asset.sizeBytes ? `${(asset.sizeBytes / (1024 * 1024)).toFixed(2)} MB` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredStock.map((img, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition group flex flex-col justify-between"
            >
              <div className="relative h-36 overflow-hidden bg-slate-100">
                <img
                  src={img.url}
                  alt={img.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
                <button
                  onClick={() => handleCopy(img.url)}
                  className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-slate-900/80 hover:bg-slate-900 text-white shadow text-[10px] font-semibold flex items-center gap-1 transition"
                >
                  {copiedUrl === img.url ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  {copiedUrl === img.url ? 'Kopyalandı' : 'URL Kopyala'}
                </button>
              </div>
              <div className="p-2.5 text-slate-700 text-xs">
                <div className="font-semibold truncate">{img.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={deleteDialog.open}
        title="Görseli Silmek İstediğinize Emin Misiniz?"
        description={`"${deleteDialog.assetName}" dosyası medya kütüphanesinden kalıcı olarak silinecektir.`}
        variant="destructive"
        confirmText={deleteDialog.loading ? 'Siliniyor...' : 'Evet, Sil'}
        cancelText="Vazgeç"
        loading={deleteDialog.loading}
        onConfirm={handleExecuteDelete}
        onCancel={() => {
          if (!deleteDialog.loading) {
            setDeleteDialog({ open: false, assetId: '', assetName: '', loading: false });
          }
        }}
      />
    </div>
  );
};
